/**
 * « Faire payer par un proche » (migration 0312).
 *
 * L'élève crée une demande (`create_skillcard_pay_request`) et partage le lien
 * `/payer/{token}` à qui il veut (parent, proche, ami). La personne paie sans
 * compte (edge fn publique `create-skillcard-gift-checkout`) ; l'élève est
 * prévenu par notification dès que le paiement est reçu.
 */
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CARD_PUBLIC_ORIGIN,
  formatEuros,
  stripMath,
  type SkillcardOfferKind,
} from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

// RPC postérieures aux types générés.
const db = supabase as unknown as SupabaseClient;

export type PayRequestStatus =
  | 'pending'
  | 'paid'
  | 'cancelled'
  | 'expired'
  | 'unavailable'
  | 'owned';

export interface PayRequestQuote {
  kind: SkillcardOfferKind;
  label: string;
  level: string | null;
  subject: string | null;
  total_cards: number;
  cards_to_buy: number;
  price_cents: number;
  unit_total_cents: number;
  savings_pct: number;
}

export interface PayRequest {
  status: PayRequestStatus;
  first_name: string;
  kind: SkillcardOfferKind;
  card_id: string;
  paid_at: string | null;
  quote: PayRequestQuote | null;
}

/** Lien à partager (origine FIXE : jamais `capacitor://` en natif). */
export function payRequestUrl(token: string): string {
  return `${CARD_PUBLIC_ORIGIN}/payer/${token}`;
}

/** Texte du partage, adressé à la personne qui va payer. */
export function payRequestShareText(label: string, priceCents: number): string {
  return `Tu peux m’offrir « ${stripMath(label)} » sur JeRaisonne (${formatEuros(priceCents)}) ? Paiement sécurisé, sans compte :`;
}

export async function createPayRequest(params: {
  kind: SkillcardOfferKind;
  targetId: string;
  cardId: string;
}): Promise<{ token: string; label: string; price_cents: number }> {
  const { data, error } = await db.rpc('create_skillcard_pay_request', {
    p_kind: params.kind,
    p_target_id: params.targetId,
    p_card_id: params.cardId,
  });
  if (error) throw new Error(payRequestErrorCode(error.message));
  return data as { token: string; label: string; price_cents: number };
}

/** Code d'erreur lisible depuis le message PostgreSQL (`raise exception 'x'`). */
export function payRequestErrorCode(message: string): string {
  const known = [
    'account_required',
    'already_owned',
    'not_for_sale',
    'rate_limited',
    'invalid_kind',
  ];
  return known.find((k) => message.includes(k)) ?? 'unknown';
}

export function payRequestErrorMessage(code: string): string {
  switch (code) {
    case 'already_owned':
      return 'Tu as déjà toutes ces cartes dans ton deck.';
    case 'not_for_sale':
      return 'Cette offre n’est plus disponible.';
    case 'rate_limited':
      return 'Tu as créé beaucoup de liens aujourd’hui. Réessaie demain.';
    case 'account_required':
      return 'Connecte-toi avec ton compte pour demander.';
    default:
      return 'Le lien n’a pas pu être créé. Réessaie dans un instant.';
  }
}

export const payRequestKey = (token: string) => ['skillcard-pay-request', token] as const;

export function usePayRequest(token: string, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: payRequestKey(token),
    queryFn: async (): Promise<PayRequest | null> => {
      const { data, error } = await db.rpc('get_skillcard_pay_request', { p_token: token });
      if (error) throw new Error(error.message);
      return (data as PayRequest | null) ?? null;
    },
    refetchInterval: (q) => (opts.poll && q.state.data?.status !== 'paid' ? 2000 : false),
  });
}

/** Paiement par le proche : renvoie l'URL Stripe Checkout. */
export async function startGiftCheckout(token: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-skillcard-gift-checkout', {
    body: { token },
  });
  const url = (data as { url?: string } | null)?.url;
  if (url) return url;
  let code = (data as { error?: string } | null)?.error ?? null;
  const ctx = (error as { context?: Response } | null)?.context;
  if (!code && ctx && typeof ctx.json === 'function') {
    try {
      code = ((await ctx.json()) as { error?: string }).error ?? null;
    } catch {
      code = null;
    }
  }
  throw new Error(code ?? 'unknown');
}

export function giftCheckoutErrorMessage(code: string): string {
  switch (code) {
    case 'already_paid':
      return 'C’est déjà payé, merci !';
    case 'already_owned':
      return 'Ces cartes sont déjà dans son deck.';
    case 'expired':
    case 'not_found':
      return 'Ce lien n’est plus valable. Demande-lui de t’en renvoyer un.';
    case 'not_for_sale':
      return 'Cette offre n’est plus disponible.';
    default:
      return 'Le paiement n’a pas pu démarrer. Réessaie dans un instant.';
  }
}
